import { useMemo, useCallback } from 'react'
import { useEngagementNavigation } from '@/contexts/engagement-navigation-context'

/**
 * Hook to create navigation props for form sections
 * Handles navigation between visible sections, accounting for hidden sections
 */
export function useFormSectionNavigation(
  sectionId: string,
  hiddenSections: Record<string, boolean>
) {
  const { sections, goToSection } = useEngagementNavigation()
  
  // Calculate visible sections
  const visibleSections = useMemo(() =>
    sections.filter(section => !hiddenSections[section.id]),
    [sections, hiddenSections]
  )
  
  // Calculate current index once to avoid repeated scans
  const currentIndex = useMemo(
    () => visibleSections.findIndex(s => s.id === sectionId),
    [visibleSections, sectionId]
  )
  
  // Derive next and previous section IDs
  const nextId = currentIndex !== -1 && currentIndex < visibleSections.length - 1
    ? visibleSections[currentIndex + 1].id
    : null
  
  const prevId = currentIndex > 0
    ? visibleSections[currentIndex - 1].id
    : null
  
  // Navigation handlers
  const onPrevious = useCallback(() => {
    if (prevId) goToSection(prevId)
  }, [prevId, goToSection])
  
  const onNext = useCallback(() => {
    if (nextId) goToSection(nextId)
  }, [nextId, goToSection])
  
  // Check if navigation is available
  const hasPrevious = prevId !== null
  const hasNext = nextId !== null
  
  return useMemo(() => ({
    onPrevious,
    onNext,
    hasPrevious,
    hasNext
  }), [onPrevious, onNext, hasPrevious, hasNext])
}

